<template>
  <div
    v-if="props.modal.isOpen"
    ref="modalElRef"
    :class="`modal-wrapper ${modal.isSidebar ? 'modal-wrapper--sidebar' : ''}`"
    :id="`${props.modal.name}-modal`"
    :key="props.modal.name"
    role="dialog"
    aria-modal="true"
    :aria-label="props.modal.name"
    tabindex="-1"
    @keydown.tab="onTabKey"
    @keydown.esc="onEscapeKey"
  >
    <div class="modal-backdrop" @click="!props.noExitOnBackdrop && onExitHandler()"></div>
    <div class="modal-container">
      <div class="number-steps-container" v-if="props.steps.length > 1">
        <div
          class="modal-number-step"
          :class="{ active: index <= activeStepComponentRef.index }"
          v-for="(step, index) in props.steps"
          :key="index"
        >
          <span class="step-name">{{ step.stepName }}</span>
          <div class="step-bar"></div>
        </div>
      </div>
      <component :is="activeStepComponentRef.step?.component" :on-exit-handler="onExitHandler" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, nextTick, ref, watch } from 'vue'
import type { VNodeRef } from 'vue/types/vnode'

import type { ModalStoreType } from '@/features/ui/store/modal/modal.store'
import modalsStore from '@/features/ui/store/modal/modal.store'
import { selectGetCurrentRoute } from '@/store/route/route.selector'
import routeStore from '@/store/route/route.store'
import type { ComponentFactoryType } from '@/types'

// TODO 4.1.2 : aria-label reprend le nom interne de la modale (jargon anglais
// lu tel quel par le lecteur d'écran). À remplacer par un aria-labelledby
// visant le titre visible de l'étape courante, dans la PR « noms ».
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

const currentRouteRef = selectGetCurrentRoute(routeStore)

const modalElRef = ref<VNodeRef | undefined>()
const previousFocusRef = ref<HTMLElement | null>(null)

const activeStepRef = computed(() => props.modal.activeStep)
const activeStepComponentRef = computed(() => {
  const activeStep = props.steps.find((step) => step.id === activeStepRef.value) ?? props.steps[0]
  const index = props.steps.findIndex((step) => step.id === activeStepRef.value)
  return { step: activeStep, index }
})

function modalEl(): HTMLElement | undefined {
  return modalElRef.value as unknown as HTMLElement | undefined
}

/** Éléments réellement atteignables au clavier dans la modale (visibles, non désactivés). */
function focusableElements(): HTMLElement[] {
  const el = modalEl()
  if (!el) return []
  return Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (node) => node.getClientRects().length > 0 && node.getAttribute('aria-hidden') !== 'true',
  )
}

// function to handle all the callbacks when exiting the modal
function onExitCallback(callback?: () => void) {
  modalsStore.toggleModal(props.modal.name, false)
  callback?.()
  props.onExit?.()
}

// force close modal when route changes
watch(currentRouteRef, () => {
  if (props.modal.isOpen) {
    onExitCallback()
  }
})

// close modal animation + callback when exiting
function onExitHandler(callback?: () => void) {
  const el = modalEl()
  if (!el) {
    onExitCallback(callback)
    return
  }
  el.classList.add('modal-wrapper--transitioning-out')
  setTimeout(() => {
    el.classList.remove('modal-wrapper--transitioning-out')
    onExitCallback(callback)
  }, 240) // call the callback after the transition ends (-10ms for safety)
}

const props = defineProps<{
  modal: ModalStoreType
  onExit?: () => void
  noExitOnBackdrop?: boolean
  steps: { component: ComponentFactoryType; id: ModalStoreType['activeStep']; stepName: string | null }[]
}>()

/**
 * WCAG 2.1.2 — le focus ne doit pas s'échapper vers l'arrière-plan tant que la
 * modale est ouverte, et doit pouvoir en sortir au clavier (Échap).
 */
function onTabKey(event: KeyboardEvent) {
  const el = modalEl()
  if (!el) return

  const items = focusableElements()
  if (items.length === 0) {
    // Rien à atteindre : on garde le focus sur le conteneur plutôt que de le
    // laisser repartir dans le fond.
    event.preventDefault()
    el.focus()
    return
  }

  const first = items[0]
  const last = items[items.length - 1]
  const active = document.activeElement as HTMLElement | null
  const isInside = active !== null && el.contains(active)

  if (event.shiftKey) {
    if (!isInside || active === first || active === el) {
      event.preventDefault()
      last.focus()
    }
    return
  }

  if (!isInside || active === last || active === el) {
    event.preventDefault()
    first.focus()
  }
}

function onEscapeKey() {
  // Les modales volontairement non-dismissables (noExitOnBackdrop) gardent
  // leur bouton d'action comme seule sortie.
  if (props.noExitOnBackdrop) return
  onExitHandler()
}

watch(
  () => props.modal.isOpen,
  (isOpen) => {
    if (isOpen) {
      previousFocusRef.value = document.activeElement as HTMLElement | null
      nextTick(() => {
        const target = focusableElements()[0] ?? modalEl()
        target?.focus()
      })
      return
    }

    if (previousFocusRef.value) {
      previousFocusRef.value.focus()
      previousFocusRef.value = null
    }
  },
)
</script>

<style lang="scss">
@import './modal.scss';
</style>
